<div class="data-explorer-container">
  <!-- Chart Area -->
  <div class="chart-area">
    <div class="chart-header">
      <h2 class="chart-title">{{ 'monitoring.data-explorer.title' | translate }}</h2>
      @if (hasData() && !loading()) {
        <div class="flex items-center gap-2">
          <p-button size="small" [outlined]="true" severity="secondary" icon="fa-solid fa-file-image" label="PNG"
                    [pTooltip]="'measurement.chart.download-png' | translate" (onClick)="exportPng()" />
          <p-button size="small" [outlined]="true" severity="secondary" icon="fa-solid fa-file-csv" label="CSV"
                    [pTooltip]="'measurement.chart.download-csv' | translate" (onClick)="exportCsv()" />
        </div>
      }
    </div>

    @if (hasData() && showLegend()) {
      <cosmos-chart-legend [items]="legendItems()" (toggle)="onLegendToggle($event)" />
    }
    
    <div class="chart-wrapper">
      @if (loading()) {
        <div class="loading-overlay">
          <p-progress-spinner strokeWidth="4" />
        </div>
      }

      @if (!hasData() && !loading()) {
        <div class="empty-state">
          <i class="fa-solid fa-chart-line"></i>
          <p>{{'monitoring.data-explorer.empty-chart' | translate}}</p>
        </div>
      }

      <canvas #chartCanvas [hidden]="!hasData() || loading()"></canvas>
    </div>

    @if (errorMessage()) {
      <p-message severity="error" >
        {{ errorMessage() | translate }}
      </p-message>
    }

    @if (diagnostics(); as diags) {
      <p-panel [header]="'monitoring.data-explorer.debug.title' | translate" [toggleable]="true" styleClass="mt-3">
        @for (d of diags; track d.variable) {
          <div class="mb-4">
            <div class="font-semibold text-surface-900 dark:text-surface-0 mb-2">
              {{ d.variable }} · {{ (d.axis === 'primary' ? 'monitoring.data-explorer.debug.primary' : 'monitoring.data-explorer.debug.secondary') | translate }}
            </div>

            @if (d.error) {
              <p-message severity="error" styleClass="mb-2">{{ d.error }}</p-message>
            }

            <div class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1">{{'monitoring.data-explorer.debug.measurements' | translate}}
              <span class="text-xs">({{ d.measurements.length }}, {{ d.withoutRows }} {{'monitoring.data-explorer.debug.without-rows' | translate}})</span>
            </div>
            <table class="w-full text-sm mb-3">
              <thead>
                <tr class="text-left text-surface-500 dark:text-surface-400">
                  <th class="py-1 pr-2">{{'monitoring.data-explorer.debug.name' | translate}}</th>
                  <th class="py-1 pr-2 text-right">{{'monitoring.data-explorer.debug.rows' | translate}}</th>
                  <th class="py-1 text-right">{{'monitoring.data-explorer.debug.values' | translate}}</th>
                </tr>
              </thead>
              <tbody>
                @for (m of d.measurements; track m._id) {
                  <tr [class]="m.rows === 0 ? 'text-red-600 dark:text-red-400' : 'text-surface-900 dark:text-surface-0'">
                    <td class="py-0.5 pr-2">{{ m.name }}</td>
                    <td class="py-0.5 pr-2 text-right">{{ m.rows }}</td>
                    <td class="py-0.5 text-right">{{ m.values }}</td>
                  </tr>
                }
              </tbody>
            </table>

            <div class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1">{{'monitoring.data-explorer.debug.stage-counts' | translate}}</div>
            <table class="w-full text-sm mb-3">
              <thead>
                <tr class="text-left text-surface-500 dark:text-surface-400">
                  <th class="py-1 pr-2">#</th>
                  <th class="py-1 pr-2">{{'monitoring.data-explorer.debug.stage' | translate}}</th>
                  <th class="py-1 pr-2 text-right">{{'monitoring.data-explorer.debug.rows' | translate}}</th>
                  <th class="py-1 text-right">Δ</th>
                </tr>
              </thead>
              <tbody>
                @for (c of d.counts; track c.index) {
                  <tr [class]="c.error ? 'text-red-600 dark:text-red-400' : c.dropped ? 'text-amber-600 dark:text-amber-400' : 'text-surface-900 dark:text-surface-0'">
                    <td class="py-0.5 pr-2">{{ c.index }}</td>
                    <td class="py-0.5 pr-2 font-mono">{{ c.stage }}</td>
                    <td class="py-0.5 pr-2 text-right">{{ c.count === null ? '—' : c.count }}</td>
                    <td class="py-0.5 text-right">{{ c.error ? c.error : (c.delta === null ? '' : (c.delta > 0 ? '+' + c.delta : c.delta)) }}</td>
                  </tr>
                }
              </tbody>
            </table>

            <div class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1">{{'monitoring.data-explorer.debug.request' | translate}}</div>
            <pre class="text-xs overflow-auto max-h-48 p-2 mb-3 rounded bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-0">{{ d.requestJson }}</pre>

            <div class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1">{{'monitoring.data-explorer.debug.pipeline' | translate}}</div>
            <pre class="text-xs overflow-auto max-h-96 p-2 rounded bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-0">{{ d.pipelineJson }}</pre>
          </div>
        }
      </p-panel>
    }
  </div>
  
  <!-- Controls Panel -->
  <div class="controls-panel">
    
    <form [formGroup]="explorerForm">
      <h3 class="controls-title flex items-center gap-2">
        {{'monitoring.data-explorer.controls.title' | translate}}
        <i class="fa-solid fa-circle-info text-base cursor-help text-surface-500 dark:text-surface-400"
           [pTooltip]="'monitoring.data-explorer.controls.info' | translate"
           tooltipPosition="left"></i>
        @if (querySummary()) {
          <button pButton type="button" class="ml-auto" size="small" severity="secondary" [text]="true"
                  [icon]="queryCollapsed() ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'"
                  [attr.aria-expanded]="!queryCollapsed()"
                  [attr.aria-label]="'monitoring.data-explorer.controls.toggle' | translate"
                  (click)="queryCollapsed.set(!queryCollapsed())"></button>
        }
      </h3>

      <!-- Collapsed: a bordered summary of what was set up (click to expand) -->
      @if (queryCollapsed() && querySummary(); as summary) {
        <div class="flex flex-col gap-1 mb-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700 cursor-pointer"
             (click)="queryCollapsed.set(false)">
          <span class="text-sm text-surface-700 dark:text-surface-200">{{ summary.text }}</span>
          @if (summary.badges.length) {
            <div class="flex flex-wrap gap-1">
              @for (badge of summary.badges; track badge) {
                <span class="text-xs rounded-full px-2 py-0.5 bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-200">{{ badge }}</span>
              }
            </div>
          }
        </div>
      }

      <!-- Expanded: the controls exactly as before (hidden, not destroyed, when collapsed so form state/options stay put) -->
      <div [class.hidden]="queryCollapsed()">
      <!-- SECTION 1: DATA SELECTION -->
      <div class="section-label mb-2">{{'monitoring.data-explorer.controls.section-data.title' | translate}}</div>
      
      <!-- Measurement Type -->
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="measurementType"
            id="measurementType"
            [options]="typeOptions()"
            optionLabel="label"
            optionValue="value"
            appendTo="body"
            placeholder="{{'monitoring.labels.measurementType' | translate}}"
            [showClear]="true"
            fluid/>
          <label for="measurementType">{{'monitoring.labels.measurementType' | translate}}</label>
        </p-iftalabel>
      </div>

      <!-- Variable to Display -->
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="metricField"
            id="metricField"
            [options]="variableOptions()"
            [group]="true"
            optionLabel="label"
            optionValue="key"
            optionGroupLabel="label"
            optionGroupChildren="items"
            appendTo="body"
            placeholder="{{'monitoring.data-explorer.controls.section-data.metric-field-placeholder' | translate}}"
            fluid/>
          <label for="metricField">{{'monitoring.data-explorer.controls.section-data.metric-field' | translate}}</label>
        </p-iftalabel>
      </div>
      @if (canAddSecondary()) {
        <div class="control-group mb-2">
          @if (!secondaryEnabled()) {
            <button pButton type="button" size="small" severity="secondary" [outlined]="true"
                    icon="fa-solid fa-plus"
                    [label]="'monitoring.data-explorer.controls.secondary.add' | translate"
                    (click)="enableSecondary()"></button>
          } @else {
            <div class="flex items-center gap-2">
              <p-iftalabel class="flex-1 min-w-0">
                <p-select
                  formControlName="secondaryMetricField"
                  id="secondaryMetricField"
                  [options]="secondaryOptions()"
                  [group]="true"
                  optionLabel="label"
                  optionValue="key"
                  optionGroupLabel="label"
                  optionGroupChildren="items"
                  appendTo="body"
                  fluid/>
                <label for="secondaryMetricField">{{'monitoring.data-explorer.controls.secondary.label' | translate}}</label>
              </p-iftalabel>
              <button pButton type="button" icon="fa-solid fa-times" [text]="true" severity="danger" size="small"
                      (click)="disableSecondary()"></button>
            </div>
          }
        </div>
      }
      
      <!-- Filtering Section -->
      <div class="filter-section mb-2">
        <div class="flex justify-between items-center mb-2">
          <span class="filter-label">{{'monitoring.data-explorer.controls.section-data.filter.title' | translate}}</span>
          @if (canAddFilter()) {
            <button pButton type="button" 
                    icon="fa-solid fa-plus" 
                    size="small"
                    (click)="addFilter()">
            </button>
          }
        </div>
        
        @if (filtersArray.length === 0) {
          <span class="no-filters-hint">{{'monitoring.data-explorer.controls.section-data.filter.no-filters' | translate}}</span>
        }
        
        @if (filtersArray.length > 1) {
          <div class="flex justify-center mb-2">
            <p-selectbutton 
              [options]="filterLogicOptions" 
              formControlName="filterLogic"
              optionLabel="label" 
              optionValue="key"
              size="small"/>
          </div>
        }
        
        <div formArrayName="filters">
          @for (filter of filtersArray.controls; track $index) {
            <div class="filter-row mb-2" [formGroupName]="$index">
              <div class="flex flex-column align-center flex-wrap gap-2">
                <div class="flex flex-row w-full">
                    <p-select 
                    formControlName="field"
                    [options]="getAvailableFilterFields($index)"
                    optionLabel="label"
                    appendTo="body"
                    placeholder="{{'monitoring.data-explorer.controls.section-data.filter.field-placeholder' | translate}}"
                    (onChange)="onFilterFieldChange($index)"
                    size="small"
                    fluid/>
                    <button pButton type="button" 
                            icon="fa-solid fa-times" 
                            [text]="true"
                            severity="danger"
                            size="small"
                            (click)="removeFilter($index)">
                    </button>
                </div>                
                @if (getFilterField($index); as field) {
                  @if (field.filterType === 'categorical') {
                    @if (filterStates[$index]?.loading) {
                      <p-progress-spinner strokeWidth="4" [style]="{width: '20px', height: '20px'}"/>
                    } @else {
                      <p-multiselect 
                        formControlName="selectedValues"
                        [options]="filterStates[$index]?.availableValues || []"
                        optionLabel="value"
                        optionValue="value"
                        appendTo="body"
                        placeholder="{{'monitoring.data-explorer.controls.section-data.filter.value-placeholder' | translate}}"
                        [showClear]="true"
                        size="small"
                        fluid/>
                    }
                  }
                  
                  @if (field.filterType === 'numeric') {
                    <p-select 
                      formControlName="operator"
                      [options]="numericOperators"
                      optionLabel="label"
                      optionValue="key"
                      appendTo="body"
                      size="small"
                      class="filter-operator"
                      fluid/>
                    <p-inputnumber 
                      formControlName="numericValue"
                      [showButtons]="false"
                      size="small"
                      class="filter-numeric-input"
                      fluid/>
                  }
                }       
              </div>
            </div>
          }
        </div>
      </div>
      
      <!-- Grouping -->
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="groupByField"
            id="groupByField"
            [options]="fields()"
            optionLabel="label"
            appendTo="body"
            [showClear]="true"
            placeholder="{{'monitoring.data-explorer.controls.section-grouping.group-by-placeholder' | translate}}" 
            fluid/>
          <label for="groupByField">{{'monitoring.data-explorer.controls.section-grouping.group-by' | translate}}</label>
        </p-iftalabel>
      </div>

      @if (explorerForm.get('groupByField')?.value) {
        <div class="control-group mb-2">
          <p-iftalabel>
            <p-select 
              formControlName="groupByField2"
              id="groupByField2"
              [options]="getAvailableGroupBy2Fields()"
              optionLabel="label"
              appendTo="body"
              [showClear]="true"
              placeholder="{{'monitoring.data-explorer.controls.section-grouping.group-by-placeholder' | translate}}" 
              fluid/>
            <label for="groupByField2">{{'monitoring.data-explorer.controls.section-grouping.group-by' | translate}} (2)</label>
          </p-iftalabel>
        </div>
      }

      <p-divider></p-divider>
      
      <!-- SECTION 2: DATE RANGE -->
      <div class="section-label mb-2">{{'monitoring.data-explorer.controls.section-data.date-range' | translate}}</div>
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-datepicker 
            formControlName="dateRange"
            selectionMode="range"
            id="dateRange"
            [showIcon]="true"
            [showButtonBar]="true"
            appendTo="body"
            dateFormat="dd/mm/yy"
            placeholder="{{'monitoring.data-explorer.controls.section-data.date-range-placeholder' | translate}}"
            fluid/>
          <label for="dateRange">{{'monitoring.data-explorer.controls.section-data.date-range' | translate}}</label>
        </p-iftalabel>
      </div>

      <p-divider></p-divider>
            
      <!-- SECTION 3: AGGREGATION -->
      <div class="section-label mb-2">{{'monitoring.data-explorer.controls.section-aggregation.title' | translate}}</div>
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="timeAggregation"
            id="timeAggregation"
            [options]="timeAggregations"
            optionLabel="label"
            appendTo="body"
            placeholder="{{'monitoring.data-explorer.controls.section-aggregation.time-aggregation-placeholder' | translate}}"
            fluid/>
          <label for="timeAggregation">{{'monitoring.data-explorer.controls.section-aggregation.time-aggregation' | translate}}</label>
        </p-iftalabel>
      </div>
      
      @if (showAggregationFunction()) {
        <div class="control-group mb-2">
          <p-iftalabel>
            <p-select 
              formControlName="aggregation"
              id="aggregation"
              [options]="aggregationFunctions"
              optionLabel="label"
              appendTo="body"
              placeholder="{{'monitoring.data-explorer.controls.section-aggregation.aggregation-function-placeholder' | translate}}"
              fluid/>
            <label for="aggregation">{{'monitoring.data-explorer.controls.section-aggregation.aggregation-function' | translate}}</label>
          </p-iftalabel>
        </div>
      }
      
      @if (debugAllowed) {
        <div class="flex items-center gap-2 mb-2">
          <p-checkbox formControlName="debug" [binary]="true" inputId="explorer-debug" />
          <label for="explorer-debug" class="text-sm">{{'monitoring.data-explorer.debug.toggle' | translate}}</label>
        </div>
      }

      </div>

      <div class="apply-button">
        <p-button 
          label="{{'monitoring.data-explorer.controls.apply' | translate}}"
          icon="fa-solid fa-play"
          [loading]="loading()"
          [disabled]="explorerForm.invalid"
          class="w-full"
          (onClick)="onApply()" />
      </div>
    </form>

    @if (hasData()) {
      <h3 class="controls-title">{{'monitoring.data-explorer.display.title' | translate}}</h3>
      <cosmos-explorer-axis-range
        idPrefix="explorer-y"
        [title]="('monitoring.data-explorer.display.left-axis' | translate) + ': ' + leftAxisTitle()"
        [config]="yRange()"
        (configChange)="onRangeChange('y', $event)" />
      @if (hasSecondaryResult()) {
        <cosmos-explorer-axis-range
          idPrefix="explorer-y2"
          [title]="('monitoring.data-explorer.display.right-axis' | translate) + ': ' + rightAxisTitle()"
          [config]="y2Range()"
          (configChange)="onRangeChange('y2', $event)" />
      }
    }
  </div>
</div>
